import type { DesktopUpdateState } from "@cleo/contracts";
import { useState } from "react";

import { isElectron } from "~/env";
import { useDesktopUpdateState } from "~/state/desktopUpdate";
import { canUpdateDesktopNow, updateDesktopNow } from "./desktopUpdate.action";
import {
  deskUpdateButtonLabel,
  deskUpdateButtonTitle,
  getDesktopUpdateDownloadedVersion,
  isDeskUpdateButtonBusy,
  resolveDesktopUpdateButtonAction,
  shouldShowDeskUpdateButton,
} from "./desktopUpdate.logic";
import { toastManager } from "./ui/toast";

/**
 * Can 30: the update waiting for the person, left of the bell. The notice
 * pops up once per version; after it is closed this is the reminder, and its
 * click is the notice's Update. It reads the update state main keeps, so it
 * stands across window closes and restarts and is gone once Cleo reopens on
 * the new version.
 */
function UpdateButton({ state }: { readonly state: DesktopUpdateState }) {
  const [pending, setPending] = useState(false);
  const busy = pending || isDeskUpdateButtonBusy(state, canUpdateDesktopNow());
  const label = deskUpdateButtonLabel(state);
  const title = deskUpdateButtonTitle(state);

  const update = () => {
    if (busy) return;
    setPending(true);
    void updateDesktopNow(
      resolveDesktopUpdateButtonAction(state),
      getDesktopUpdateDownloadedVersion(state),
    )
      .catch((error: unknown) => {
        toastManager.add({
          type: "error",
          title: "Could not update Cleo",
          description: error instanceof Error ? error.message : "The update did not start.",
        });
      })
      .finally(() => setPending(false));
  };

  return (
    <button
      type="button"
      aria-label={`${label} — ${title}`}
      // Never disabled: the rail's controls stay in the Tab order (the bell's law).
      aria-disabled={busy || undefined}
      title={title}
      className="ncl-ticker-glass relative flex h-8 shrink-0 items-center rounded-full px-3 text-xs font-medium text-foreground hover:text-foreground aria-disabled:cursor-default"
      data-shell-utility="update"
      onClick={update}
    >
      {label}
      <span
        className="absolute right-0 top-0 size-2 rounded-full bg-primary ring-2 ring-background"
        aria-hidden="true"
      />
    </button>
  );
}

export function DeskUpdateButton() {
  const state = useDesktopUpdateState();
  if (!isElectron || !state || !shouldShowDeskUpdateButton(state)) return null;
  return <UpdateButton state={state} />;
}
